import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'error'

interface ToastData {
  id: string
  title: string
  description?: string
  tone?: ToastTone
  /** Alias for tone — accepted but ignored internally; use tone instead. */
  variant?: string
  durationMs?: number
}

interface ToastContextValue {
  toast: (data: Omit<ToastData, 'id'>) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const toneClasses: Record<ToastTone, string> = {
  neutral: 'border-gray-200',
  success: 'border-green-300',
  warning: 'border-amber-300',
  danger: 'border-red-300',
  error: 'border-red-300',
  info: 'border-blue-300',
}

const toneIcon: Record<ToastTone, ReactNode> = {
  neutral: <Info className="h-4 w-4 text-gray-500" />,
  success: <CheckCircle2 className="h-4 w-4 text-green-600" />,
  warning: <AlertTriangle className="h-4 w-4 text-amber-600" />,
  danger: <AlertCircle className="h-4 w-4 text-red-600" />,
  error: <AlertCircle className="h-4 w-4 text-red-600" />,
  info: <Info className="h-4 w-4 text-blue-600" />,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback((data: Omit<ToastData, 'id'>) => {
    const id = Math.random().toString(36).slice(2)
    setToasts((prev) => [...prev, { id, tone: 'neutral', durationMs: 4000, ...data }])
  }, [])

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            duration={t.durationMs}
            onOpenChange={(open) => !open && dismiss(t.id)}
            className={cn(
              'bg-white rounded-md border shadow-popover p-3 pr-8 relative',
              'data-[state=open]:animate-slide-in-top data-[state=closed]:animate-fade-in',
              toneClasses[t.tone ?? 'neutral'],
            )}
          >
            <div className="flex items-start gap-2">
              <span className="shrink-0 mt-0.5">{toneIcon[t.tone ?? 'neutral']}</span>
              <div className="flex flex-col gap-0.5 min-w-0">
                <ToastPrimitive.Title className="text-body font-medium text-gray-900">
                  {t.title}
                </ToastPrimitive.Title>
                {t.description ? (
                  <ToastPrimitive.Description className="text-small text-gray-600">
                    {t.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
            </div>
            <ToastPrimitive.Close
              aria-label="Schließen"
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 p-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            >
              <X className="h-3.5 w-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)] z-toast outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
